import { CheckboxField, Radio, RadioGroupField, TextField } from '@aws-amplify/ui-react';
import { useEffect, useState } from 'react';
import { TbCircle, TbMailCheck, TbUser, TbUsers } from 'react-icons/tb';
import { RevalidateEmailsSlideout } from '../../../custom-components/RevalidateEmailsSlideout';
import { SelectableBoxGrid } from '../../../custom-components/SelectableBoxGrid';
import { InvalidEmailHandling } from '../../../types/export.types';
import { AccountAssociationMode, ContactsWithoutCompaniesBehavior, SalesforceExportObject } from '../../../types/salesforce.types';
import { ExportRangeSelector } from './ExportRangeSelector';
import { EXPORT_NOTIFICATIONS_DISABLED, EXPORT_NOTIFICATIONS_DISABLED_MESSAGE } from './exportNotifications';

export interface ExportOptionsProps {
  value: Record<string, any>;
  onChange: (options: Record<string, any>) => void;
  target: string;
  disabled?: boolean;
  estimatedCount?: number;
  onValidChange?: (isValid: boolean) => void;
  savedSearchName?: string;
  userFirstName?: string;
  rangeFrom?: number | null;
  rangeTo?: number | null;
  onRangeChange?: (range: { rangeFrom?: number; rangeTo?: number }) => void;
  onRangeValidChange?: (isValid: boolean) => void;
}

export function ContactExportOptions({ value, onChange, target, disabled, estimatedCount, onValidChange, savedSearchName, userFirstName, rangeFrom, rangeTo, onRangeChange, onRangeValidChange }: ExportOptionsProps) {
  const [showRevalidateInfo, setShowRevalidateInfo] = useState(false);
  const [revalidateEmailsProvider, setRevalidateEmailsProvider] = useState<string>(value.revalidateEmailsProvider ?? 'NONE');
  const [invalidEmailHandling, setInvalidEmailHandling] = useState<InvalidEmailHandling>(value.invalidEmailHandling ?? InvalidEmailHandling.SKIP);
  const [sendNotification, setSendNotification] = useState<boolean>(value.sendNotification ?? false);
  const [createHubspotList, setCreateHubspotList] = useState<boolean>(value.createHubspotList ?? false);
  const [hubspotListName, setHubspotListName] = useState<string>(value.hubspotListName ?? '');
  const [contactsWithoutCompaniesBehavior, setContactsWithoutCompaniesBehavior] = useState<ContactsWithoutCompaniesBehavior>(
    value.contactsWithoutCompaniesBehavior ?? ContactsWithoutCompaniesBehavior.IMPORT_ANYWAY
  );
  const [salesforceObject, setSalesforceObject] = useState<SalesforceExportObject | null>(
    value.salesforceObject ?? null
  );
  const [accountAssociationMode, setAccountAssociationMode] = useState<AccountAssociationMode>(
    value.accountAssociationMode ?? AccountAssociationMode.MULTIPLE
  );

  // Sync options to parent whenever any value changes
  useEffect(() => {
    // Forced off while disabled, so re-saving an older export cannot leave it asking for a
    // notification that will never arrive.
    const options: Record<string, any> = {
      revalidateEmailsProvider,
      sendNotification: EXPORT_NOTIFICATIONS_DISABLED ? false : sendNotification
    };

    if (revalidateEmailsProvider !== 'NONE') {
      options.invalidEmailHandling = invalidEmailHandling;
    }

    if (target === 'HUBSPOT') {
      options.contactsWithoutCompaniesBehavior = contactsWithoutCompaniesBehavior;
      options.createHubspotList = createHubspotList;
      if (createHubspotList && hubspotListName.trim()) {
        options.hubspotListName = hubspotListName.trim();
      }
      options.accountAssociationMode = accountAssociationMode;
    }

    if (target === 'SALESFORCE') {
      if (salesforceObject) {
        options.salesforceObject = salesforceObject;
      }
      // Contact exports only support SKIP strategy (link to existing accounts only)
      options.accountStrategy = 'SKIP';
      // Leads are never linked to Accounts, so the mode is meaningless for them
      if (salesforceObject === 'Contact') {
        options.accountAssociationMode = accountAssociationMode;
      }
    }

    onChange(options);
  }, [
    revalidateEmailsProvider,
    invalidEmailHandling,
    sendNotification,
    createHubspotList,
    hubspotListName,
    contactsWithoutCompaniesBehavior,
    salesforceObject,
    accountAssociationMode,
    target
  ]);

  // Report validation status to parent
  useEffect(() => {
    if (!onValidChange) return;

    // For Salesforce, salesforceObject must be selected
    if (target === 'SALESFORCE' && !salesforceObject) {
      onValidChange(false);
      return;
    }

    onValidChange(true);
  }, [target, salesforceObject, onValidChange]);

  const isHubspot = target === 'HUBSPOT';
  const showAssociationMode = isHubspot || (target === 'SALESFORCE' && salesforceObject === 'Contact');
  // HubSpot calls them companies, Salesforce calls them accounts
  const associationNoun = isHubspot ? 'company' : 'account';

  return (
    <>
      {/* Email Validation Section */}
      <div className="export-step__section">
        <h3 className="export-step__section-heading">Re-Validate Emails</h3>
        <div className="desc-2" style={{ marginBottom: 15 }}>
          Run a fresh email validation check before export to ensure you have up-to-date deliverability status.{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); setShowRevalidateInfo(true); }}>Learn more</a>
        </div>
        <SelectableBoxGrid
          options={[
            { id: 'NONE', label: 'Skip', icon: <TbCircle />, iconColor: (disabled && revalidateEmailsProvider !== 'NONE') ? 'var(--amplify-colors-neutral-40)' : 'var(--amplify-colors-purple-60)', iconSize: 48, disabled: disabled && revalidateEmailsProvider !== 'NONE' },
            { id: 'ZEROBOUNCE', label: 'ZeroBounce', icon: <TbMailCheck />, iconColor: (disabled && revalidateEmailsProvider !== 'ZEROBOUNCE') ? 'var(--amplify-colors-neutral-40)' : 'var(--amplify-colors-green-60)', iconSize: 48, disabled: disabled && revalidateEmailsProvider !== 'ZEROBOUNCE' },
          ]}
          value={revalidateEmailsProvider}
          onChange={(id) => setRevalidateEmailsProvider(id || 'NONE')}
          isSelectable={!disabled}
        />

        {revalidateEmailsProvider !== 'NONE' && (
          <RadioGroupField
            legend="What to do with invalid emails"
            name="invalidEmailHandling"
            value={invalidEmailHandling}
            onChange={(e) => setInvalidEmailHandling(e.target.value as InvalidEmailHandling)}
            style={{ marginTop: 20 }}
            isDisabled={disabled}
          >
            <Radio value={InvalidEmailHandling.SKIP}>
              Skip exporting contacts with invalid emails
            </Radio>
            <div className="desc-2" style={{ marginLeft: 24, marginTop: -5, marginBottom: 5 }}>
              Only export contacts with valid or catch-all emails. Contacts with invalid emails will be excluded from the export entirely.
            </div>
            <Radio value={InvalidEmailHandling.INCLUDE}>
              Export all contacts including those with invalid emails
            </Radio>
            <div className="desc-2" style={{ marginLeft: 24, marginTop: -5 }}>
              All contacts will be exported. To see which emails are invalid, include the Email Verification Status field in your field mappings.
            </div>
          </RadioGroupField>
        )}
      </div>

      {/* HubSpot Options Section */}
      {target === 'HUBSPOT' && (
        <div className="export-step__section">
          <h3 className="export-step__section-heading">HubSpot Options</h3>

          <div>
            <CheckboxField
              label="Add contacts to a new HubSpot static list"
              name="createHubspotList"
              checked={createHubspotList}
              onChange={(e) => {
                const checked = e.target.checked;
                setCreateHubspotList(checked);
                if (checked && !hubspotListName) {
                  const now = new Date();
                  const dateStr = now.toLocaleDateString();
                  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                  const userPrefix = userFirstName ? `${userFirstName}'s ` : '';
                  const searchSuffix = savedSearchName ? ` for ${savedSearchName}` : '';
                  if (userPrefix || savedSearchName) {
                    setHubspotListName(`${userPrefix}${dateStr} @ ${timeStr} export${searchSuffix}`);
                  } else {
                    setHubspotListName(`Edulead Export - ${dateStr} @ ${timeStr}`);
                  }
                }
              }}
              isDisabled={disabled}
            />
            {createHubspotList && (
              <div style={{ marginLeft: 25, marginTop: 10, maxWidth: 800 }}>
                <TextField
                  label="List name"
                  value={hubspotListName}
                  onChange={(e) => setHubspotListName(e.target.value)}
                  size="small"
                  maxLength={250}
                  isDisabled={disabled}
                />
              </div>
            )}
          </div>
        </div>
      )}


      {/* Salesforce Options Section */}
      {target === 'SALESFORCE' && (
        <div className="export-step__section">
          <h3 className="export-step__section-heading">Salesforce Object Type</h3>
          <SelectableBoxGrid
            options={[
              {
                id: 'Lead',
                label: 'Lead',
                icon: <TbUsers />,
                iconColor: (disabled && salesforceObject !== 'Lead') ? 'var(--amplify-colors-neutral-40)' : 'var(--amplify-colors-purple-60)',
                iconSize: 48,
                disabled: disabled && salesforceObject !== 'Lead'
              },
              {
                id: 'Contact',
                label: 'Contact',
                icon: <TbUser />,
                iconColor: (disabled && salesforceObject !== 'Contact') ? 'var(--amplify-colors-neutral-40)' : 'var(--amplify-colors-yellow-60)',
                iconSize: 48,
                disabled: disabled && salesforceObject !== 'Contact'
              }
            ]}
            value={salesforceObject}
            onChange={(id) => setSalesforceObject(id as SalesforceExportObject | null)}
            isSelectable={!disabled}
          />
        </div>
      )}

      {/* Account Associations - HubSpot, and Salesforce Contacts only; Leads are never linked to Accounts */}
      {showAssociationMode && (
        <div className="export-step__section">
          <h3 className="export-step__section-heading">{isHubspot ? 'Company' : 'Account'} Associations</h3>
          <RadioGroupField
            legend={`How should contacts be linked to ${associationNoun === 'company' ? 'companies' : 'accounts'}?`}
            name="accountAssociationMode"
            value={accountAssociationMode}
            onChange={(e) => setAccountAssociationMode(e.target.value as AccountAssociationMode)}
            isDisabled={disabled}
          >
            <Radio value={AccountAssociationMode.MULTIPLE}>
              Link to multiple {associationNoun === 'company' ? 'companies' : 'accounts'}
            </Radio>
            <div className="desc-2" style={{ marginLeft: 24, marginTop: -5, marginBottom: 5 }}>
              {isHubspot ? (
                <>Each contact is linked to every school and district we know they work at.</>
              ) : (
                <>
                  The contact's district becomes their main account, and each school they work at is
                  linked as a related contact. Needs "Allow users to relate a contact to multiple
                  accounts" enabled in Salesforce.
                </>
              )}
            </div>
            <Radio value={AccountAssociationMode.SINGLE}>
              Link to one {associationNoun} only
            </Radio>
            <div className="desc-2" style={{ marginLeft: 24, marginTop: -5 }}>
              Each contact is linked to a single {associationNoun}: the school they work at, or their
              district when we know no school. Other institutions we know about are not linked.
            </div>
          </RadioGroupField>
        </div>
      )}

      {/* Record Range Section */}
      {onRangeChange && (
        <ExportRangeSelector
          estimatedCount={estimatedCount ?? 0}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          onChange={onRangeChange}
          onValidChange={onRangeValidChange}
          disabled={disabled}
        />
      )}

      {/* Notifications Section */}
      <div className="export-step__section">
        <h3 className="export-step__section-heading">Notifications</h3>
        {/* The wrapper carries the tooltip: a disabled input fires no mouse events of its own. */}
        <span
          data-tooltip-id={EXPORT_NOTIFICATIONS_DISABLED ? 'main-tooltip' : undefined}
          data-tooltip-content={EXPORT_NOTIFICATIONS_DISABLED ? EXPORT_NOTIFICATIONS_DISABLED_MESSAGE : undefined}
          style={EXPORT_NOTIFICATIONS_DISABLED ? { cursor: 'help', display: 'inline-block' } : undefined}
        >
          <CheckboxField
            label="Send me an email when the export completes"
            name="sendNotification"
            checked={EXPORT_NOTIFICATIONS_DISABLED ? false : sendNotification}
            onChange={(e) => setSendNotification(e.target.checked)}
            isDisabled={disabled || EXPORT_NOTIFICATIONS_DISABLED}
          />
        </span>
      </div>

      <RevalidateEmailsSlideout
        isOpen={showRevalidateInfo}
        onClose={() => setShowRevalidateInfo(false)}
      />
    </>
  );
}
